import { useState, useEffect } from 'react'
import moment from 'moment'
import { Popover } from '@material-ui/core'
import { Text, Button, Skeleton, useSnackbarContext } from 'shared/ui'
import { getEventList, getTaskList, updateTask } from 'apps/calendar/api'
import EventRow from 'apps/calendar/components/EventRow'
import TaskRow from 'apps/calendar/components/TaskRow'

// Anchored under the navbar's Calendar/Tasks icon buttons (src/navigation/NavigationBar.js),
// same spot the Notification bell's own popover opens from — but built as a raw MUI
// `Popover` (not the shared `MenuPopover`, which is rigidly Menu/MenuItem-based with no
// header-row/"View All" support), following the same template as
// apps/leads/pages/LeadDetail/AddConversationPicker.js.
//
// IMPORTANT: this renders from src/navigation/, OUTSIDE the calendar module's own
// isolated Redux store (apps/calendar/store/configureStore.js — only wrapped around
// /calendar/* routes/sidebars/modals). So data here comes from the PLAIN API functions
// (apps/calendar/api — not the redux action creators of the same name in
// apps/calendar/store/actions), fetched fresh every time the popover opens, with no
// caching/refresh-on-mutate wiring. Row clicks still work fine via
// appContext.showSideBar, which is store-agnostic.
const TITLES = { events: "Today's Events", tasks: "Today's Tasks" }
const EMPTY_TEXT = { events: 'No events today.', tasks: 'No tasks today.' }
const ERROR_TEXT = { events: "Couldn't load today's events.", tasks: "Couldn't load today's tasks." }
// Same top-right spot the app's other notification snackbars (App.js) use.
const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'right' }
const VIEW_ALL_PATH = { events: '/calendar/events', tasks: '/calendar/tasks' }

function TodayPopover({ kind, anchorEl, onClose, appContext, history, meUid }) {
  const [records, setRecords] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const { showSnackbar } = useSnackbarContext()

  useEffect(() => {
    if (!anchorEl || !meUid) return

    setIsLoading(true)
    setHasError(false)

    if (kind === 'events') {
      const start = moment().startOf('day').toISOString()
      const end = moment().endOf('day').toISOString()
      getEventList(start, end, { user: String(meUid) }, 0, 50).then(({ ok, data }) => {
        setIsLoading(false)
        if (ok) setRecords(data?.records || [])
        else setHasError(true)
      })
    } else {
      // due_end (server-side, added since the last check of this endpoint) covers "not due
      // after today" — no due_start, so overdue tasks from any point in the past come back
      // too. Open/In Progress only, same default TaskPage's own filter uses. sort=due_at
      // (also new) puts overdue tasks first.
      const dueEnd = moment().format('YYYY-MM-DD')
      getTaskList({ user: String(meUid), status: 'open,in_progress', due_end: dueEnd, sort: 'due_at' }, 0, 250).then(({ ok, data }) => {
        setIsLoading(false)
        if (ok) setRecords(data?.records || [])
        else setHasError(true)
      })
    }
  }, [kind, anchorEl, meUid])

  const handleViewAll = () => {
    onClose()
    history.push(VIEW_ALL_PATH[kind])
  }

  // Ticking a task's checkbox completes it. Optimistic: the row flips to done (checked +
  // struck through) right away and flips back if the update fails. The row stays in the
  // list until the popover is next opened (the refetch's open/in_progress filter drops it).
  // Other already-open calendar views aren't refreshed — this popover has no access to
  // that module's store (see the note at the top of this file).
  const setTaskStatus = (uid, status) => (
    setRecords(prev => prev.map(r => (r.uid === uid ? { ...r, status } : r)))
  )
  const completeTask = (task) => {
    if (task.status === 'completed') return
    const previousStatus = task.status
    setTaskStatus(task.uid, 'completed')
    updateTask(task.uid, { status: 'completed' }).then(({ ok }) => {
      if (ok) {
        showSnackbar('Task marked as complete', { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR })
      } else {
        setTaskStatus(task.uid, previousStatus)
        showSnackbar("Couldn't mark the task as complete. Please try again.", { variant: 'error', anchorOrigin: SNACKBAR_ANCHOR })
      }
    })
  }

  const openRecord = (record) => {
    if (kind === 'events')
      appContext.showSideBar('calendar', 'event-sidebar', { eventKey: record.uid })
    else
      appContext.showSideBar('calendar', 'task-sidebar', { taskKey: record.uid })
    onClose()
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div style={{ width: 360, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px' }}>
          <Text style={{ fontWeight: 700 }}>{TITLES[kind]}</Text>
          <Button variant="text" color="primary" label="View All" onClick={handleViewAll} />
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 420 }}>
          {isLoading ? (
            [0, 1, 2].map(i => (
              <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid #e0e0e0' }}>
                <Skeleton width="70%" />
                <Skeleton width="40%" />
              </div>
            ))
          ) : hasError ? (
            <Text color="textMuted" style={{ padding: '16px 14px', display: 'block' }}>{ERROR_TEXT[kind]}</Text>
          ) : records.length === 0 ? (
            <Text color="textMuted" style={{ padding: '16px 14px', display: 'block' }}>{EMPTY_TEXT[kind]}</Text>
          ) : (
            records.map(record => (
              <div key={record.uid} style={{ borderBottom: '1px solid #e0e0e0', padding: '4px 14px' }}>
                {kind === 'events'
                  ? <EventRow event={record} onClick={() => openRecord(record)} />
                  : <TaskRow task={record} onClick={() => openRecord(record)} onComplete={() => completeTask(record)} />}
              </div>
            ))
          )}
        </div>
      </div>
    </Popover>
  )
}

export default TodayPopover
